import React, {useEffect, useId, useRef, useState} from 'react'
import styled from '@emotion/styled'
import {FiBookmark, FiEdit2, FiPlus, FiTrash2} from 'react-icons/fi'
import * as API from '../../../api'

type FilterValues = Record<string, unknown>
type SavedFilter = {guid: string; name: string; filters: FilterValues}
type Props = {
  report: string
  currentFilters: FilterValues
  onSelect: (filters: FilterValues) => void
  getFiltersToSave: () => FilterValues | null
  disabled?: boolean
  selectedFilterGuid?: string
  onSelectionChange?: (guid: string) => void
}

const Panel = styled.section`
  margin-bottom: 26px;
  padding: 20px;
  border: 1px solid #d9e4fd;
  border-radius: 12px;
  background: #fff;
  color: #2b3352;
  .saved-title { display: flex; align-items: center; gap: 8px; margin: 0 0 5px; font-size: 16px; font-weight: 700; color: #264073; }
  .saved-hint { color: #67758f; font-size: 13px; margin: 0 0 15px; }
  .saved-label { display: flex; flex-direction: column; gap: 7px; font-size: 14px; font-weight: 600; margin: 0; flex: 1; min-width: 0; }
  .saved-input { width: 100%; min-height: 42px; border: 1px solid #d9e4fd; border-radius: 7px; padding: 9px 12px; background: #f6fafd; color: #2b3352; font-size: 14px; font-weight: 500; }
  .saved-input:focus-visible { outline: 2px solid #3570e6; outline-offset: 2px; }
  .saved-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 9px; margin-top: 12px; }
  .saved-button { display: inline-flex; justify-content: center; align-items: center; gap: 7px; min-height: 39px; padding: 9px 14px; border: 1px solid transparent; border-radius: 7px; background: #3570e6; color: #fff; font-size: 14px; font-weight: 600; cursor: pointer; }
  .saved-button:hover:not(:disabled) { filter: brightness(.94); }
  .saved-button:focus-visible { outline: 2px solid #264073; outline-offset: 2px; }
  .saved-button:disabled, .saved-input:disabled { cursor: not-allowed; opacity: .6; }
  .saved-create { background: #099d76; }
  .saved-secondary { background: #fff; color: #345483; border-color: #d9e4fd; }
  .saved-danger { background: #fff; color: #bc3544; border-color: #efcbd1; }
  .saved-danger-confirm { background: #bc3544; color: #fff; }
  .saved-editor, .saved-confirm { margin-top: 15px; padding-top: 15px; border-top: 1px solid #e0e5f2; }
  .saved-editor p, .saved-confirm p { font-size: 13px; margin: 0 0 12px; }
  .saved-empty { margin: 12px 0 0; color: #67758f; font-size: 14px; }
  .saved-changed { margin: 10px 0 0; color: #946400; font-size: 13px; }
  .saved-message { margin: 12px 0 0; font-size: 13px; overflow-wrap: anywhere; }
  .saved-error { color: #bc3544; }
  .saved-success { color: #087b5c; }
  @media (max-width: 600px) {
    padding: 15px;
    .saved-actions .saved-button { flex: 1 1 auto; }
  }
`

function fingerprint(value: unknown): string {
  if (Array.isArray(value)) return '[' + value.map(fingerprint).join(',') + ']'
  if (value && typeof value === 'object') {
    const fields = value as FilterValues
    // New optional multi-selects should not mark older saved filters as edited.
    const keys = Object.keys(fields).filter((key) =>
      !Array.isArray(fields[key]) || (fields[key] as unknown[]).length > 0
    )
    return '{' + keys.sort().map((key) =>
      JSON.stringify(key) + ':' + fingerprint(fields[key])
    ).join(',') + '}'
  }
  return JSON.stringify(value) ?? 'undefined'
}

function responseData<T>(response: any, fallback: string): T {
  if (!response?.success) throw new Error(response?.message || fallback)
  return response.data
}

function errorMessage(error: any, fallback: string): string {
  return error?.response?.data?.message || (error?.isAxiosError ? fallback : error?.message) || fallback
}

export default function SavedReportFilters({report, currentFilters, onSelect, getFiltersToSave, disabled = false, selectedFilterGuid, onSelectionChange}: Props) {
  const [items, setItems] = useState<SavedFilter[]>([])
  const [selectedGuid, setSelectedGuid] = useState(selectedFilterGuid || '')
  const [loading, setLoading] = useState(true)
  const [pending, setPending] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const [retry, setRetry] = useState(0)
  const [editor, setEditor] = useState<'create' | 'update' | null>(null)
  const [name, setName] = useState('')
  const [nameError, setNameError] = useState('')
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const nameErrorId = useId()
  const selectionChangeRef = useRef(onSelectionChange)
  selectionChangeRef.current = onSelectionChange
  const selectionRef = useRef(selectedFilterGuid)
  selectionRef.current = selectedFilterGuid
  const generation = useRef(0)
  const busy = useRef(false)
  const currentFiltersRef = useRef(currentFilters)
  currentFiltersRef.current = currentFilters

  useEffect(() => {
    if (selectedFilterGuid !== undefined) setSelectedGuid(selectedFilterGuid)
  }, [selectedFilterGuid])

  useEffect(() => {
    const request = ++generation.current
    busy.current = false
    setItems([])
    setSelectedGuid('')
    setLoading(true)
    setPending(false)
    setLoadFailed(false)
    setEditor(null)
    setConfirmRemove(false)
    setError('')
    setSuccess('')
    API.getSavedReportFilters(report)
      .then((response) => {
        if (generation.current !== request) return
        const rows = responseData<SavedFilter[]>(response, 'Não foi possível carregar os filtros salvos.')
        if (!Array.isArray(rows)) throw new Error('Não foi possível carregar os filtros salvos.')
        setItems(rows)
        const match = rows.find((item) => fingerprint(item.filters) === fingerprint(currentFiltersRef.current))
        const guid = selectionRef.current !== undefined
          ? (rows.some((item) => item.guid === selectionRef.current) ? selectionRef.current : '')
          : match?.guid || ''
        setSelectedGuid(guid)
        selectionChangeRef.current?.(guid)
      })
      .catch((caught) => {
        if (generation.current !== request) return
        setLoadFailed(true)
        setError(errorMessage(caught, 'Não foi possível carregar os filtros salvos.'))
      })
      .finally(() => {
        if (generation.current === request) setLoading(false)
      })
    return () => { generation.current++ }
  }, [report, retry])

  const selected = items.find((item) => item.guid === selectedGuid)
  const locked = disabled || loading || pending
  const changed = selected && fingerprint(selected.filters) !== fingerprint(currentFilters)

  function startEditor(mode: 'create' | 'update') {
    setEditor(mode)
    setName(mode === 'update' ? selected?.name || '' : '')
    setNameError('')
    setConfirmRemove(false)
    setError('')
    setSuccess('')
  }

  async function save() {
    if (locked || busy.current || !editor) return
    const trimmedName = name.trim()
    if (!trimmedName || trimmedName.length > 100) {
      setNameError(!trimmedName ? 'Informe um nome para o filtro.' : 'Use até 100 caracteres no nome.')
      return
    }
    const filters = getFiltersToSave()
    if (!filters) return
    if (editor === 'update' && !selected) return
    const request = generation.current
    const mode = editor
    busy.current = true
    setPending(true)
    setError('')
    setSuccess('')
    try {
      const response = mode === 'update'
        ? await API.updateSavedReportFilter(report, selected!.guid, {name: trimmedName, filters})
        : await API.createSavedReportFilter(report, {name: trimmedName, filters})
      if (generation.current !== request) return
      const item = responseData<SavedFilter>(response, 'Não foi possível salvar o filtro.')
      if (!item?.guid || !item?.filters) throw new Error('Não foi possível salvar o filtro.')
      setItems((current) => mode === 'update'
        ? current.map((existing) => existing.guid === item.guid ? item : existing)
        : [...current, item])
      setSelectedGuid(item.guid)
      onSelectionChange?.(item.guid)
      setEditor(null)
      setSuccess(mode === 'update' ? 'Filtro atualizado com sucesso.' : 'Filtro salvo com sucesso.')
    } catch (caught) {
      if (generation.current === request) setError(errorMessage(caught, 'Não foi possível salvar o filtro.'))
    } finally {
      if (generation.current === request) {
        busy.current = false
        setPending(false)
      }
    }
  }

  async function remove() {
    if (locked || busy.current || !selected) return
    const request = generation.current
    const guid = selected.guid
    busy.current = true
    setPending(true)
    setError('')
    setSuccess('')
    try {
      const response = await API.deleteSavedReportFilter(report, guid)
      if (generation.current !== request) return
      responseData(response, 'Não foi possível remover o filtro.')
      setItems((current) => current.filter((item) => item.guid !== guid))
      setSelectedGuid('')
      onSelectionChange?.('')
      setConfirmRemove(false)
      setSuccess('Filtro removido. Os campos atuais foram mantidos.')
    } catch (caught) {
      if (generation.current === request) setError(errorMessage(caught, 'Não foi possível remover o filtro.'))
    } finally {
      if (generation.current === request) {
        busy.current = false
        setPending(false)
      }
    }
  }

  return <Panel aria-label='Filtros salvos do relatório' aria-busy={loading || pending}>
    <h3 className='saved-title'><FiBookmark aria-hidden='true' /> Filtros salvos</h3>
    <p className='saved-hint'>Guarde combinações de filtros para usar novamente neste relatório.</p>
    {loading && <p role='status' className='saved-empty'>Carregando filtros salvos...</p>}
    {!loading && !loadFailed && <>
      {items.length > 0 ? <label className='saved-label'>
        Selecionar filtro salvo
        <select className='saved-input' value={selectedGuid} disabled={locked} onChange={(event) => {
          const guid = event.target.value
          setSelectedGuid(guid)
          onSelectionChange?.(guid)
          setEditor(null)
          setConfirmRemove(false)
          setError('')
          setSuccess('')
          const item = items.find((entry) => entry.guid === guid)
          if (item) onSelect(JSON.parse(JSON.stringify(item.filters)))
        }}>
          <option value=''>Selecione um filtro salvo</option>
          {items.map((item) => <option key={item.guid} value={item.guid}>{item.name}</option>)}
        </select>
      </label> : <p className='saved-empty'>Nenhum filtro salvo no momento.</p>}
      {changed && <p className='saved-changed' role='status'>Alterações não salvas neste filtro.</p>}
      {!editor && !confirmRemove && <div className='saved-actions'>
        {selected && <>
          <button type='button' className='saved-button' disabled={locked} onClick={() => startEditor('update')}><FiEdit2 aria-hidden='true' /> Atualizar</button>
          <button type='button' className='saved-button saved-danger' disabled={locked} onClick={() => {
            setConfirmRemove(true)
            setError('')
            setSuccess('')
          }}><FiTrash2 aria-hidden='true' /> Remover</button>
        </>}
        <button type='button' className='saved-button saved-create' disabled={locked} onClick={() => startEditor('create')}>
          <FiPlus aria-hidden='true' /> {items.length ? 'Novo filtro' : 'Salvar filtro atual'}
        </button>
      </div>}
      {editor && <div className='saved-editor'>
        <p>{editor === 'update' ? 'Atualize o nome e salve os campos atuais neste filtro.' : 'Dê um nome à combinação atual de filtros.'}</p>
        <label className='saved-label'>Nome do filtro
          <input className='saved-input' value={name} maxLength={100} autoFocus disabled={locked}
            placeholder='Ex.: Pedidos aprovados do mês' aria-invalid={Boolean(nameError)} aria-describedby={nameError ? nameErrorId : undefined}
            onChange={(event) => { setName(event.target.value); setNameError('') }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') { event.preventDefault(); event.stopPropagation(); save() }
            }} />
        </label>
        {nameError && <p id={nameErrorId} role='alert' className='saved-message saved-error'>{nameError}</p>}
        <div className='saved-actions'>
          <button type='button' className='saved-button saved-create' disabled={locked} onClick={save}>
            {pending ? 'Salvando...' : editor === 'update' ? 'Salvar alterações' : 'Salvar filtro'}
          </button>
          <button type='button' className='saved-button saved-secondary' disabled={locked} onClick={() => { setEditor(null); setError(''); setNameError('') }}>Cancelar</button>
        </div>
      </div>}
      {confirmRemove && selected && <div className='saved-confirm'>
        <p>Remover o filtro <strong>{selected.name}</strong>? Os campos atuais serão mantidos.</p>
        <div className='saved-actions'>
          <button type='button' className='saved-button saved-danger-confirm' disabled={locked} onClick={remove}>{pending ? 'Removendo...' : 'Confirmar remoção'}</button>
          <button type='button' className='saved-button saved-secondary' disabled={locked} onClick={() => { setConfirmRemove(false); setError('') }}>Cancelar</button>
        </div>
      </div>}
    </>}
    {error && <p role='alert' className='saved-message saved-error'>{error}</p>}
    {loadFailed && <div className='saved-actions'><button type='button' className='saved-button saved-secondary' disabled={disabled} onClick={() => setRetry((value) => value + 1)}>Tentar novamente</button></div>}
    {success && <p role='status' className='saved-message saved-success'>{success}</p>}
  </Panel>
}


